<script lang="ts">
	/**
	 * An `<a>` that knows about the router.
	 *
	 * Plain anchors do a full page load, which throws away the SPA state and
	 * re-runs the auth handshake. This wires `use:link` so in-app paths are
	 * pushed onto the history stack instead, and `use:active` so the link marks
	 * itself when it points at the current route — that is what makes nav items
	 * light up without every caller re-deriving `$location`.
	 *
	 * External destinations cannot go through `use:link` (the action throws on
	 * anything that does not start with `/`), so they take a plain-anchor branch
	 * and get `target="_blank"`, `rel="noopener noreferrer"` and the
	 * `ExternalLinkIcon` — the visual promise that the click leaves the app.
	 *
	 * ```svelte
	 * <RoutedLink to="/Users" class="text-primary-600 hover:underline">Users</RoutedLink>
	 * <RoutedLink to="https://abstract3d.com">Abstract 3D</RoutedLink>
	 * ```
	 */

	import * as SPA from '../abstract-spa-router';
	import spaActive from '../abstract-spa-router/active';
	import ExternalLinkIcon from './ExternalLinkIcon.svelte';

	interface Props {
		/** Destination. A path (`/Users`) routes in-app; anything else is external. */
		to?: string;
		/** Alias for `to`, so the component drops in where an `href` was written. */
		href?: string | undefined;
		/** Class added while the destination matches the current route. */
		activeClass?: string;
		/**
		 * Route pattern that counts as "active", when it differs from the
		 * destination. A section link needs `activePath="/Settings/*"` so it stays
		 * lit on every sub-page while still navigating to `/Settings` itself.
		 */
		activePath?: string | undefined;
		/** Class added while it does not match. Empty adds nothing. */
		inactiveClass?: string;
		/** Forces the external treatment for a destination that would route in-app. */
		external?: boolean;
		/** Overrides the automatic external glyph — set false on icon-only links. */
		showExternalIcon?: boolean | undefined;
		/** Anchor target. Defaults to `_blank` for external destinations. */
		target?: string | undefined;
		/** Extra classes for the anchor. */
		class?: string | undefined;
		/** Forwarded click handler. */
		onclick?: (_e: MouseEvent) => void;
		children?: import('svelte').Snippet;
		/** Remaining attributes are spread onto the anchor. */
		[key: string]: unknown;
	}

	let {
		to = '/',
		href = undefined,
		activeClass = 'active',
		activePath = undefined,
		inactiveClass = '',
		external = false,
		showExternalIcon = undefined,
		target = undefined,
		class: cssClass = undefined,
		onclick = undefined,
		children,
		...restProps
	}: Props = $props();

	/** Resolved destination: prefer `href` when provided, fall back to `to`. */
	let destination = $derived(href ?? to);

	/*
	 * A destination is "internal" only when the router can actually handle it —
	 * `use:link` throws for hrefs not starting with '/', so mailto:, tel:, https:
	 * and protocol-relative URLs all have to bypass it.
	 */
	let isExternal = $derived(external || !destination.startsWith('/') || destination.startsWith('//'));
	let resolvedTarget = $derived(target ?? (isExternal ? '_blank' : undefined));
	/* Only `_blank` needs the reverse-tabnabbing guard; leaving `rel` unset
	   otherwise keeps the rendered markup clean. */
	let resolvedRel = $derived(resolvedTarget === '_blank' ? 'noopener noreferrer' : undefined);
	let withIcon = $derived(showExternalIcon ?? isExternal);
</script>

{#if isExternal}
	<a
		{...restProps}
		href={destination}
		target={resolvedTarget}
		rel={resolvedRel}
		class={cssClass ?? ''}
		{onclick}
	>
		{@render children?.()}{#if withIcon}<ExternalLinkIcon
				class="ml-1 inline h-3 w-3 align-baseline"
			/>{/if}
	</a>
{:else}
	<a
		{...restProps}
		use:SPA.link
		use:spaActive={{
			path: activePath ?? destination,
			className: activeClass,
			inactiveClassName: inactiveClass
		}}
		href={destination}
		target={resolvedTarget}
		rel={resolvedRel}
		class={cssClass ?? ''}
		{onclick}
	>
		{@render children?.()}{#if withIcon}<ExternalLinkIcon
				class="ml-1 inline h-3 w-3 align-baseline"
			/>{/if}
	</a>
{/if}
